import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const InvestorsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investmentRange: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Animation refs
  const heroRef = useScrollAnimation();
  const commitmentLeftRef = useScrollAnimation();
  const commitmentCard1Ref = useScrollAnimation({ delay: 0 });
  const commitmentCard2Ref = useScrollAnimation({ delay: 100 });
  const commitmentCard3Ref = useScrollAnimation({ delay: 200 });
  const commitmentCard4Ref = useScrollAnimation({ delay: 300 });
  const processHeadingRef = useScrollAnimation();
  const processStep1Ref = useScrollAnimation({ delay: 0 });
  const processStep2Ref = useScrollAnimation({ delay: 100 });
  const processStep3Ref = useScrollAnimation({ delay: 200 });
  const processStep4Ref = useScrollAnimation({ delay: 300 });
  const reportingHeadingRef = useScrollAnimation();
  const reportingCard1Ref = useScrollAnimation({ delay: 0 });
  const reportingCard2Ref = useScrollAnimation({ delay: 100 });
  const reportingCard3Ref = useScrollAnimation({ delay: 200 });
  const formRef = useScrollAnimation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('investmentRange', formData.investmentRange);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6q61a6a739gopmlh520', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', investmentRange: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center bg-[#1F2A44] mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}></div>
        </div>
        <div ref={heroRef} className="relative max-w-[1440px] mx-auto px-20 w-full text-center">
          <div className="w-16 h-0.5 bg-[#B89C4B] mx-auto mb-6"></div>
          <h1
            className="text-[56px] text-white leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Investor Relations
          </h1>
          <p
            className="text-[18px] text-white/85 max-w-[640px] mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Building long-term partnerships through transparency, alignment, and disciplined execution
          </p>
        </div>
      </section>

      {/* Investment Approach Section */}
      <section className="bg-white py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div ref={commitmentLeftRef}>
              <h2
                className="text-[42px] text-[#1F2A44] leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Our Commitment to Capital Partners
              </h2>
              <div className="space-y-6">
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  We view our investors as true partners in building long-term value. This relationship is built on transparency, regular communication, and alignment of interests.
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Our investment approach prioritizes capital preservation and risk-adjusted returns over aggressive growth. Every acquisition undergoes rigorous underwriting and stress testing to ensure resilience across market cycles.
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  We believe in structured reporting, honest assessment of performance, and proactive communication about both opportunities and challenges.
                </p>
                <p
                  className="text-[16px] text-[#2E2E2E] leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Our focus on operational improvements and disciplined asset management creates sustainable value that benefits all stakeholders over the long term.
                </p>
              </div>
            </div>

            <div className="space-y-7">
              <div
                ref={commitmentCard1Ref}
                className="flex items-start bg-[#F5F5F3] p-6 rounded-lg"
              >
                <div className="flex-shrink-0 w-12 h-12 mr-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-xl text-[#B89C4B]"></i>
                </div>
                <div>
                  <h3
                    className="text-[19px] text-[#1F2A44] font-semibold mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Capital Preservation
                  </h3>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Conservative underwriting and disciplined leverage protect investor capital through market cycles.
                  </p>
                </div>
              </div>

              <div
                ref={commitmentCard2Ref}
                className="flex items-start bg-[#F5F5F3] p-6 rounded-lg"
              >
                <div className="flex-shrink-0 w-12 h-12 mr-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                  <i className="ri-file-chart-line text-xl text-[#B89C4B]"></i>
                </div>
                <div>
                  <h3
                    className="text-[19px] text-[#1F2A44] font-semibold mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Transparent Reporting
                  </h3>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Regular financial updates, property performance metrics, and clear communication.
                  </p>
                </div>
              </div>

              <div
                ref={commitmentCard3Ref}
                className="flex items-start bg-[#F5F5F3] p-6 rounded-lg"
              >
                <div className="flex-shrink-0 w-12 h-12 mr-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                  <i className="ri-team-line text-xl text-[#B89C4B]"></i>
                </div>
                <div>
                  <h3
                    className="text-[19px] text-[#1F2A44] font-semibold mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Alignment of Interests
                  </h3>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Co-investment and performance-based compensation ensure our success is tied to yours.
                  </p>
                </div>
              </div>

              <div
                ref={commitmentCard4Ref}
                className="flex items-start bg-[#F5F5F3] p-6 rounded-lg"
              >
                <div className="flex-shrink-0 w-12 h-12 mr-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-xl text-[#B89C4B]"></i>
                </div>
                <div>
                  <h3
                    className="text-[19px] text-[#1F2A44] font-semibold mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Long-Term Focus
                  </h3>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Patient capital approach focused on sustainable value creation rather than short-term gains.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Process Section */}
      <section className="bg-[#F5F5F3] py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <h2
            ref={processHeadingRef}
            className="text-[44px] text-[#1F2A44] text-center mb-14"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            How We Work With Investors
          </h2>

          <div className="relative">
            <div className="absolute top-8 left-0 right-0 h-0.5 bg-[#B89C4B] hidden lg:block"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div ref={processStep1Ref} className="relative">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#B89C4B] flex items-center justify-center shadow-md relative z-10">
                    <span
                      className="text-[22px] text-white font-bold"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      1
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <h3
                    className="text-[18px] text-[#1F2A44] font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Initial Discussion
                  </h3>
                  <p
                    className="text-[14px] text-[#2E2E2E] leading-relaxed max-w-[240px] mx-auto"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    We begin with a conversation to understand your investment goals, timeline, and preferences.
                  </p>
                </div>
              </div>

              <div ref={processStep2Ref} className="relative">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#B89C4B] flex items-center justify-center shadow-md relative z-10">
                    <span
                      className="text-[22px] text-white font-bold"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      2
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <h3
                    className="text-[18px] text-[#1F2A44] font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Deal Review
                  </h3>
                  <p
                    className="text-[14px] text-[#2E2E2E] leading-relaxed max-w-[240px] mx-auto"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Qualified investors receive detailed information on current and upcoming opportunities.
                  </p>
                </div>
              </div>

              <div ref={processStep3Ref} className="relative">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#B89C4B] flex items-center justify-center shadow-md relative z-10">
                    <span
                      className="text-[22px] text-white font-bold"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      3
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <h3
                    className="text-[18px] text-[#1F2A44] font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Due Diligence
                  </h3>
                  <p
                    className="text-[14px] text-[#2E2E2E] leading-relaxed max-w-[240px] mx-auto"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    We provide comprehensive property analysis, financial projections, and market research.
                  </p>
                </div>
              </div>

              <div ref={processStep4Ref} className="relative">
                <div className="flex flex-col items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-[#B89C4B] flex items-center justify-center shadow-md relative z-10">
                    <span
                      className="text-[22px] text-white font-bold"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      4
                    </span>
                  </div>
                </div>
                <div className="text-center">
                  <h3
                    className="text-[18px] text-[#1F2A44] font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Partnership
                  </h3>
                  <p
                    className="text-[14px] text-[#2E2E2E] leading-relaxed max-w-[240px] mx-auto"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Upon investment, you receive regular updates, financial reports, and ongoing communication.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reporting & Transparency Section */}
      <section className="bg-[#1F2A44] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={reportingHeadingRef} className="max-w-[880px] mx-auto text-center mb-12">
            <h2
              className="text-[40px] text-white leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Transparent Reporting
            </h2>
            <p
              className="text-[16px] text-white/85 leading-relaxed mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              We believe investors deserve clear, consistent communication about property performance, market conditions, and strategic decisions.
            </p>
            <p
              className="text-[16px] text-white/85 leading-relaxed"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Our reporting framework provides the information you need to understand how your capital is being deployed and how properties are performing relative to projections.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              ref={reportingCard1Ref}
              className="bg-white/8 border border-white/15 p-8 rounded-lg text-center"
            >
              <div className="w-14 h-14 mx-auto mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-calendar-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[18px] text-white font-semibold mb-3"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Regular Updates
              </h3>
              <p
                className="text-[15px] text-white/80 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
              >
                Quarterly financial reports with detailed property performance metrics and market commentary.
              </p>
            </div>

            <div
              ref={reportingCard2Ref}
              className="bg-white/8 border border-white/15 p-8 rounded-lg text-center"
            >
              <div className="w-14 h-14 mx-auto mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-bar-chart-box-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[18px] text-white font-semibold mb-3"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Performance Tracking
              </h3>
              <p
                className="text-[15px] text-white/80 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
              >
                Clear comparison of actual results versus underwriting projections and business plan milestones.
              </p>
            </div>

            <div
              ref={reportingCard3Ref}
              className="bg-white/8 border border-white/15 p-8 rounded-lg text-center"
            >
              <div className="w-14 h-14 mx-auto mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                <i className="ri-phone-line text-2xl text-[#B89C4B]"></i>
              </div>
              <h3
                className="text-[18px] text-white font-semibold mb-3"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Direct Access
              </h3>
              <p
                className="text-[15px] text-white/80 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
              >
                Open communication channels for questions, concerns, or requests for additional information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Inquiry Form Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={formRef} className="max-w-[800px] mx-auto">
            <div className="text-center mb-12">
              <h2
                className="text-[38px] text-[#1F2A44] mb-5"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Explore Partnership Opportunities
              </h2>
              <p
                className="text-[16px] text-[#2E2E2E]"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Complete the form below to learn more about current and upcoming investment opportunities
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="investor-inquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="investmentRange"
                    className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Investment Range
                  </label>
                  <select
                    id="investmentRange"
                    name="investmentRange"
                    value={formData.investmentRange}
                    onChange={(e) => setFormData({ ...formData, investmentRange: e.target.value })}
                    className="w-full h-13 px-4 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors cursor-pointer"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <option value="">Select Range</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k+">$500,000+</option>
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-[14px] text-[#1F2A44] font-medium mb-2"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D9D9D6] rounded text-[16px] focus:outline-none focus:border-[#B89C4B] transition-colors resize-none"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                  placeholder="Tell us about your investment goals and interests..."
                ></textarea>
                <p className="text-[13px] text-[#2E2E2E]/60 mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {formData.message.length}/500 characters
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded">
                  <p className="text-[15px] text-green-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Thank you for your inquiry. We will be in touch shortly.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded">
                  <p className="text-[15px] text-red-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    There was an error submitting your inquiry. Please try again.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InvestorsPage;